import { z } from 'zod';
import {
  QualityAdaptiveProposalSchema,
  QualityAutomationValueSummarySchema,
  QualityEvidencePlanSummarySchema,
} from './quality-strategy';

/**
 * `pending-quality-hq` v0 — Quality HQ wire types.
 *
 * Design: docs/handbook/features/pending/quality-hq.md
 * Locked decisions: 2026-08-17.
 */

export const QUALITY_RISK_SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
export type QualityRiskSeverity = (typeof QUALITY_RISK_SEVERITIES)[number];

export const QUALITY_RISK_DOMAINS = ['revenue', 'security', 'availability', 'compliance', 'brand', 'data'] as const;
export type QualityRiskDomain = (typeof QUALITY_RISK_DOMAINS)[number];

export const QUALITY_RISK_STATUSES = ['active', 'accepted', 'retired'] as const;
export type QualityRiskStatus = (typeof QUALITY_RISK_STATUSES)[number];

export const QUALITY_RISK_SOURCES = [
  'manual',
  'seeded_from_flow',
  'seeded_from_demo',
  'proposed_from_story',
  'proposed_from_prod',
] as const;
export type QualityRiskSource = (typeof QUALITY_RISK_SOURCES)[number];

export const QUALITY_FINDING_SOURCES = ['agent', 'manual', 'privacy', 'flake', 'contract'] as const;
export type QualityFindingSource = (typeof QUALITY_FINDING_SOURCES)[number];

export const QualityRiskSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  slug: z.string().min(1).max(64),
  title: z.string().min(1).max(160),
  whyItHurts: z.string().max(500),
  severity: z.enum(QUALITY_RISK_SEVERITIES),
  domain: z.enum(QUALITY_RISK_DOMAINS),
  revenueTier: z.enum(['high', 'med', 'low']).nullable(),
  ownerUserId: z.string().uuid().nullable(),
  status: z.enum(QUALITY_RISK_STATUSES),
  source: z.enum(QUALITY_RISK_SOURCES),
  flowIds: z.array(z.string().uuid()),
  storyIds: z.array(z.string().uuid()),
  uncovered: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type QualityRisk = z.infer<typeof QualityRiskSchema>;

export const CreateQualityRiskRequestSchema = z.object({
  title: z.string().min(1).max(160),
  slug: z.string().min(1).max(64).optional(),
  whyItHurts: z.string().max(500).optional(),
  severity: z.enum(QUALITY_RISK_SEVERITIES).optional(),
  domain: z.enum(QUALITY_RISK_DOMAINS).optional(),
  revenueTier: z.enum(['high', 'med', 'low']).nullable().optional(),
  flowIds: z.array(z.string().uuid()).optional(),
});
export type CreateQualityRiskRequest = z.infer<typeof CreateQualityRiskRequestSchema>;

export const PatchQualityRiskRequestSchema = z.object({
  title: z.string().min(1).max(160).optional(),
  whyItHurts: z.string().max(500).optional(),
  severity: z.enum(QUALITY_RISK_SEVERITIES).optional(),
  domain: z.enum(QUALITY_RISK_DOMAINS).optional(),
  revenueTier: z.enum(['high', 'med', 'low']).nullable().optional(),
  flowIds: z.array(z.string().uuid()).optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
  status: z.enum(QUALITY_RISK_STATUSES).optional(),
});
export type PatchQualityRiskRequest = z.infer<typeof PatchQualityRiskRequestSchema>;

export const QualityFindingSchema = z.object({
  source: z.enum(QUALITY_FINDING_SOURCES),
  sourceId: z.string(),
  title: z.string(),
  severity: z.enum(QUALITY_RISK_SEVERITIES),
  status: z.string(),
  riskId: z.string().uuid().nullable(),
  occurredAt: z.string(),
  drilldownUrl: z.string().nullable(),
  acked: z.enum(['dismissed', 'assigned']).nullable(),
});
export type QualityFinding = z.infer<typeof QualityFindingSchema>;

export const AckQualityFindingRequestSchema = z.object({
  ack: z.enum(['dismissed', 'assigned']),
  riskId: z.string().uuid().optional(),
});
export type AckQualityFindingRequest = z.infer<typeof AckQualityFindingRequestSchema>;

/**
 * The six evidence bars that render under the verdict. Each one is a
 * projection of a release tile or of the risks query — derived on read,
 * never stored, and never summed into an overall score.
 */
export const QUALITY_EVIDENCE_BAR_KINDS = [
  'business_risk',
  'risk_coverage',
  'automation_reliability',
  'production_stability',
  'defect_escape',
  'contract_health',
] as const;
export type QualityEvidenceBarKind = (typeof QUALITY_EVIDENCE_BAR_KINDS)[number];

export const QualityEvidenceBarSchema = z.object({
  kind: z.enum(QUALITY_EVIDENCE_BAR_KINDS),
  state: z.enum(['green', 'amber', 'red', 'na']),
  /** 0-100 fill. `null` exactly when the signal is unconfigured (`na`). */
  value: z.number().min(0).max(100).nullable(),
  /** Short number the bar is labelled with, e.g. `3/5`. */
  headline: z.string().nullable(),
  /** One line naming the evidence behind the number. */
  detail: z.string(),
  drilldownUrl: z.string().nullable(),
});
export type QualityEvidenceBar = z.infer<typeof QualityEvidenceBarSchema>;

export const QualityConfidenceSchema = z.object({
  verdict: z.enum(['green', 'amber', 'red']).nullable(),
  commitSha: z.string().nullable(),
  llmSummary: z.string().nullable(),
  drilldownUrl: z.string().nullable(),
  bars: z.array(QualityEvidenceBarSchema),
});
export type QualityConfidence = z.infer<typeof QualityConfidenceSchema>;

export const QualityTrendDaySchema = z.object({
  date: z.string(),
  opened: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative(),
  openEnd: z.number().int().nonnegative(),
});
export type QualityTrendDay = z.infer<typeof QualityTrendDaySchema>;

export const QualityTrendSchema = z.object({
  openFailures: z.number().int().nonnegative(),
  needsReview: z.number().int().nonnegative(),
  verifiedAfterFail: z.number().int().nonnegative(),
  medianHoursToClose: z.number().nullable(),
  days: z.array(QualityTrendDaySchema),
});
export type QualityTrend = z.infer<typeof QualityTrendSchema>;

/** Wave 6A/6B — maintenance activity metrics (honest counts; never money/time/tests saved). */
export const MAINTENANCE_ROI_STATUS = ['available', 'limited', 'insufficient_evidence', 'unavailable'] as const;
export type MaintenanceRoiStatus = (typeof MAINTENANCE_ROI_STATUS)[number];

export const MaintenanceRoiEvidenceBandSchema = z.object({
  status: z.enum(['available', 'limited', 'insufficient_evidence', 'unavailable']),
  detail: z.string().max(300).optional(),
});
export type MaintenanceRoiEvidenceBand = z.infer<typeof MaintenanceRoiEvidenceBandSchema>;

export const MaintenanceRoiMetricsSchema = z.object({
  attribution: z.literal('maintenance_roi_metrics'),
  status: z.enum(MAINTENANCE_ROI_STATUS),
  /** Framing: activity evidence — not financial ROI. */
  framing: z.literal('activity_evidence').default('activity_evidence'),
  windowDays: z.number().int().positive(),
  reviewsScanned: z.number().int().nonnegative(),
  reviewsWithDecision: z.number().int().nonnegative(),
  decisions: z.object({
    noChangeRequired: z.number().int().nonnegative(),
    manualUpdateRequired: z.number().int().nonnegative(),
    automationUpdateRequired: z.number().int().nonnegative(),
    bothUpdateRequired: z.number().int().nonnegative(),
    retestRequired: z.number().int().nonnegative(),
    dismiss: z.number().int().nonnegative(),
    updateRequiredTotal: z.number().int().nonnegative(),
  }),
  ownershipItems: z.object({
    open: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
  }),
  selectionStamps: z.object({
    reviewsWithStamp: z.number().int().nonnegative(),
    sumSelectedFlowCount: z.number().int().nonnegative(),
    sumSuiteFlowCount: z.number().int().nonnegative(),
    sumNotSelectedCount: z.number().int().nonnegative(),
    meanSelectionRatio: z.number().min(0).max(1).nullable(),
  }),
  /**
   * Wave 6B — Failure Intelligence activity in the same window (bounded).
   * Not a second FI engine; counts only.
   */
  failureActivity: z
    .object({
      status: z.enum(['available', 'limited', 'insufficient_evidence', 'unavailable']),
      openCount: z.number().int().nonnegative().default(0),
      resolvedCount: z.number().int().nonnegative().default(0),
      /** Resolved rows with verified_by set (human close). */
      verifiedClosedCount: z.number().int().nonnegative().default(0),
      /** Distinct fingerprints with ≥2 rows in window. */
      repeatedFingerprintCount: z.number().int().nonnegative().default(0),
    })
    .optional(),
  /** Explicit non-claims — always present for dashboard honesty. */
  unavailableClaims: z
    .object({
      testsSaved: MaintenanceRoiEvidenceBandSchema,
      timeSaved: MaintenanceRoiEvidenceBandSchema,
      moneySaved: MaintenanceRoiEvidenceBandSchema,
      executionDuration: MaintenanceRoiEvidenceBandSchema,
      safeSkip: MaintenanceRoiEvidenceBandSchema,
    })
    .optional(),
  deepLinks: z
    .object({
      changeImpact: z.string().max(500),
      failures: z.string().max(500),
      releaseConfidence: z.string().max(500),
    })
    .optional(),
  claimsTestsSaved: z.literal(false),
  claimsTimeSaved: z.literal(false),
  claimsMoneySaved: z.literal(false),
  claimsSafeSkip: z.literal(false),
  honestyNotes: z.array(z.string().max(300)).max(12),
});
export type MaintenanceRoiMetrics = z.infer<typeof MaintenanceRoiMetricsSchema>;

export const QualityHqPayloadSchema = z.object({
  projectId: z.string().uuid(),
  risks: z.array(QualityRiskSchema),
  findings: z.array(QualityFindingSchema),
  confidence: QualityConfidenceSchema,
  uncoveredHighRisks: z.number().int().nonnegative(),
  openFindings: z.number().int().nonnegative(),
  evidencePlan: QualityEvidencePlanSummarySchema,
  automationValue: QualityAutomationValueSummarySchema,
  adaptive: z.array(QualityAdaptiveProposalSchema),
  qualityTrend: QualityTrendSchema.optional(),
  /** Wave 6A — optional until clients refresh; compose-on-read maintenance ROI. */
  maintenanceRoi: MaintenanceRoiMetricsSchema.optional(),
});
export type QualityHqPayload = z.infer<typeof QualityHqPayloadSchema>;

export const QualityProjectSummarySchema = z.object({
  projectId: z.string().uuid(),
  verdict: z.enum(['green', 'amber', 'red']).nullable(),
  openFindings: z.number().int().nonnegative(),
  uncoveredHighRisks: z.number().int().nonnegative(),
});
export type QualityProjectSummary = z.infer<typeof QualityProjectSummarySchema>;

export const QualitySummariesResponseSchema = z.object({
  summaries: z.array(QualityProjectSummarySchema),
});
export type QualitySummariesResponse = z.infer<typeof QualitySummariesResponseSchema>;
